import React from 'react';
import { Box, Typography } from '@mui/material';
import { ScoreRing } from './ScoreRing';
import { ScoreBreakdown } from './ScoreBreakdown';
import { ScoreTotals } from './ScoreTotals';

/**
 * The banner at the top of the results view: the ring at one end of the row,
 * what the score came to at the other, and what it means in between. What to do
 * next is asked elsewhere.
 *
 * Props:
 *   id              (string)   - ID prefix for generated element IDs.
 *   correctCount    (number)   - Questions answered correctly.
 *   total           (number)   - Questions in the quiz.
 *   percentage      (number)   - This attempt's score, 0 to 100.
 *   bestScore       (object)   - The highest score recorded for this quiz.
 *   isNewHighScore  (bool)     - Whether this attempt beat that high score.
 */
export const QuizScore = ({ id, correctCount, total, percentage, bestScore, isNewHighScore }) => {
  const isPerfect = percentage === 100;
  /*
   * A submitted attempt is at least tied for best, so with no stored high score
   * this attempt is the high score. Whether it set a record is the server's
   * call — it is the only side that knows what the previous best was.
   */
  const highScore = bestScore?.percentage ?? percentage;

  return (
    <Box className={`quizScore ${isPerfect ? 'perfect' : ''}`} id={`${id}__score`}>
      <Box className="scoreCard">
        <ScoreRing id={id} percentage={percentage} />

        {/* Beside the ring: the counts, or the congratulation on a perfect run. */}
        <ScoreBreakdown id={id} correctCount={correctCount} total={total} percentage={percentage} />

        {/*
          The scores themselves keep to the far end of the row, with the call-out
          that one of them is a record directly above them.
        */}
        <Box className="scoreAside">
          {isNewHighScore && (
            <Typography className="newHighScore" id={`${id}__newHighScore`}>
              New High Score!
            </Typography>
          )}

          <ScoreTotals id={id} percentage={percentage} highScore={highScore} />
        </Box>
      </Box>
    </Box>
  );
};
